import { useEffect, useRef, useCallback } from 'react';

export interface IdleTimeoutOptions {
  timeout?: number; // Tiempo en milisegundos antes de considerar inactivo (default: 30 minutos)
  onIdle?: () => void; // Callback cuando se detecta inactividad
  events?: string[]; // Eventos a escuchar (default: eventos comunes de actividad)
  enabled?: boolean; // Si está habilitado (default: true)
}

/**
 * Hook para detectar inactividad del usuario y ejecutar acciones
 * Útil para logout automático, guardar datos, etc.
 */
export function useIdleTimeout(options: IdleTimeoutOptions = {}) {
  const {
    timeout = 30 * 60 * 1000, // 30 minutos por defecto
    onIdle,
    events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'],
    enabled = true,
  } = options;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const resetTimer = useCallback(() => {
    if (!enabled) return;

    lastActivityRef.current = Date.now();

    // Limpiar timer anterior
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Crear nuevo timer
    timeoutRef.current = setTimeout(() => {
      if (onIdle) {
        onIdle();
      }
    }, timeout);
  }, [timeout, onIdle, enabled]);

  const clearIdleTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      clearIdleTimer();
      return;
    }

    // Iniciar timer
    resetTimer();

    // Agregar listeners de eventos
    events.forEach((event) => {
      document.addEventListener(event, resetTimer, { passive: true });
    });

    // Limpiar al desmontar
    return () => {
      clearIdleTimer();
      events.forEach((event) => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [enabled, events, resetTimer, clearIdleTimer]);

  return {
    resetTimer,
    clearIdleTimer,
    getLastActivity: () => lastActivityRef.current,
    getTimeSinceLastActivity: () => Date.now() - lastActivityRef.current,
  };
}

